import { type CSSProperties, useEffect, useLayoutEffect, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import MeritoButton from '../MeritoButtons/MeritoButtons';
import styles from './SIPromoTooltip.module.scss';
import { trackSIPromoShown, trackSIPromoDismissed, trackSIPromoButtonClicked } from '../../../pages/GSTReconciliation/siPromoAmplitude';

type Props = {
  isSidebarCollapsed: boolean;
  onDismiss: () => void;
  onCheckItOut: () => void;
};

const ARROW_SIZE = 11.667261877654857;
const ARROW_TOP_OFFSET = 9.6;

const SIPromoTooltip = ({ isSidebarCollapsed, onDismiss, onCheckItOut }: Props) => {
  const [tooltipTop, setTooltipTop] = useState<number>(286);

  useEffect(() => {
    trackSIPromoShown("tooltip", "si_sidebar_tooltip", "sidebar");
  }, []);

  useLayoutEffect(() => {
    const updateTooltipPosition = () => {
      const anchor = document.querySelector('[data-si-promo-anchor="true"]');

      if (!(anchor instanceof HTMLElement)) {
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const alignedTop =
        anchorRect.top +
        anchorRect.height / 2 -
        (ARROW_TOP_OFFSET + ARROW_SIZE / 2);

      setTooltipTop(alignedTop);
    };

    updateTooltipPosition();
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [isSidebarCollapsed]);

  const handleCheckItOut = () => {
    trackSIPromoButtonClicked("Check It Out", "sidebar_tooltip");
    onDismiss();
    onCheckItOut();
  };

  const handleDismiss = () => {
    trackSIPromoDismissed("tooltip", "si_sidebar_tooltip", "sidebar");
    onDismiss();
  };

  const tooltipStyle = {
    top: `${tooltipTop}px`,
    '--si-arrow-size': `${ARROW_SIZE}px`,
    '--si-arrow-top-offset': `${ARROW_TOP_OFFSET}px`,
    '--si-arrow-half-size': `${ARROW_SIZE / 2}px`,
  } as CSSProperties;

  return (
    <div
      style={tooltipStyle}
      className={`${styles.tooltip} ${isSidebarCollapsed ? styles.collapsed : styles.expanded}`}
    >
      <div className={styles.arrow} />
      <button className={styles.closeBtn} onClick={handleDismiss} aria-label="Close">
        <AiOutlineClose size={14} />
      </button>
      <p className={styles.title}>Introducing Supplier Intelligence</p>
      <p className={styles.body}>Identify suppliers impacting your cash flow.</p>
      <MeritoButton
        type="primary-1"
        size="md"
        btnText="Check It Out"
        onClick={handleCheckItOut}
        extraStyles={{
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          minWidth: 0,
          alignSelf: 'flex-end',
        }}
      />
    </div>
  );
};

export default SIPromoTooltip;
